import { useState, useEffect } from "react";
import {
  Table,
  Badge,
  Container,
  Card,
  Row,
  Col,
  Spinner,
  Alert,
  Dropdown,
  Button,
} from "react-bootstrap";

function OrdersAdminView() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    // Fetch orders for admin view
    const fetchOrders = () => {
      setLoading(true);
      fetch(`${process.env.REACT_APP_API_BASE_URL}/orders/all-orders`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error("Failed to fetch orders");
          }
          return response.json();
        })
        .then((data) => {
          console.log(data.orders);
          setOrders(data.orders);
          setError("");
        })
        .catch((error) => {
          console.error("Error fetching orders:", error);
          setError("Failed to load orders. Please try again.");
        })
        .finally(() => {
          setLoading(false);
        });
    };

    fetchOrders();
  }, []);

  const getStatusConfig = (status) => {
    const configs = {
      Completed: { bg: "success", icon: "check-circle", text: "white" },
      Processing: { bg: "info", icon: "cog", text: "white" },
      Pending: { bg: "warning", icon: "clock", text: "dark" },
      Cancelled: { bg: "danger", icon: "times-circle", text: "white" },
      default: { bg: "secondary", icon: "question-circle", text: "white" },
    };
    return configs[status] || configs.default;
  };

  const getFilteredOrders = () => {
    if (filter === "all") return orders;
    return orders.filter(
      (order) => order.status.toLowerCase() === filter.toLowerCase()
    );
  };

  const filteredOrders = getFilteredOrders();

  const stats = {
    total: orders.length,
    pending: orders.filter((order) => order.status === "Pending").length,
    processing: orders.filter((order) => order.status === "Processing").length,
    completed: orders.filter((order) => order.status === "Completed").length,
    revenue: orders.reduce((sum, order) => sum + (order.totalPrice || 0), 0),
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center py-5">
          <div className="loading-container">
            <Spinner animation="border" variant="primary" size="lg" />
            <h5 className="mt-3 text-muted">Loading orders...</h5>
          </div>
        </div>
        <style jsx>{`
          .loading-container {
            animation: fadeIn 0.5s ease-in;
          }

          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      {/* Admin Stats Dashboard */}
      <Row className="mb-4">
        <Col lg={2} md={4} sm={6} className="mb-3">
          <Card className="admin-stats-card border-0 h-100">
            <Card.Body className="text-center">
              <div className="admin-stat-icon bg-primary bg-gradient mb-3">
                <i className="fas fa-chart-bar text-white"></i>
              </div>
              <h4 className="fw-bold text-primary">{stats.total}</h4>
              <p className="text-muted mb-0">Total Orders</p>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={2} md={4} sm={6} className="mb-3">
          <Card className="admin-stats-card border-0 h-100">
            <Card.Body className="text-center">
              <div className="admin-stat-icon bg-warning bg-gradient mb-3">
                <i className="fas fa-clock text-white"></i>
              </div>
              <h4 className="fw-bold text-warning">{stats.pending}</h4>
              <p className="text-muted mb-0">Pending</p>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={2} md={4} sm={6} className="mb-3">
          <Card className="admin-stats-card border-0 h-100">
            <Card.Body className="text-center">
              <div className="admin-stat-icon bg-info bg-gradient mb-3">
                <i className="fas fa-cog text-white"></i>
              </div>
              <h4 className="fw-bold text-info">{stats.processing}</h4>
              <p className="text-muted mb-0">Processing</p>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={2} md={4} sm={6} className="mb-3">
          <Card className="admin-stats-card border-0 h-100">
            <Card.Body className="text-center">
              <div className="admin-stat-icon bg-success bg-gradient mb-3">
                <i className="fas fa-check-circle text-white"></i>
              </div>
              <h4 className="fw-bold text-success">{stats.completed}</h4>
              <p className="text-muted mb-0">Completed</p>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4} md={8} sm={12} className="mb-3">
          <Card className="admin-stats-card border-0 h-100">
            <Card.Body className="text-center">
              <div
                className="admin-stat-icon bg-gradient mb-3"
                style={{
                  background: "linear-gradient(135deg, #6f42c1, #495057)",
                }}
              >
                <i className="fas fa-money-bill-wave text-white"></i>
              </div>
              <h4 className="fw-bold" style={{ color: "#6f42c1" }}>
                ₱
                {stats.revenue.toLocaleString("en-PH", {
                  minimumFractionDigits: 2,
                })}
              </h4>
              <p className="text-muted mb-0">Total Revenue</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filter Controls */}
      <Row className="mb-4">
        <Col>
          <Card className="filter-controls-card border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1 fw-bold">Order Management</h5>
                  <p className="text-muted mb-0">
                    {filteredOrders.length}{" "}
                    {filter === "all" ? "total" : filter} order
                    {filteredOrders.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <Dropdown>
                  <Dropdown.Toggle
                    variant="outline-primary"
                    id="filter-dropdown"
                    className="filter-btn"
                  >
                    <i className="fas fa-filter me-2"></i>
                    Filter:{" "}
                    {filter === "all"
                      ? "All Orders"
                      : filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={() => setFilter("all")}
                      active={filter === "all"}
                    >
                      <i className="fas fa-list me-2"></i>All Orders
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item
                      onClick={() => setFilter("pending")}
                      active={filter === "pending"}
                    >
                      <i className="fas fa-clock me-2 text-warning"></i>Pending
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setFilter("processing")}
                      active={filter === "processing"}
                    >
                      <i className="fas fa-cog me-2 text-info"></i>Processing
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setFilter("completed")}
                      active={filter === "completed"}
                    >
                      <i className="fas fa-check-circle me-2 text-success"></i>
                      Completed
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-4">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Orders Table */}
      {filteredOrders.length === 0 ? (
        <Card className="empty-state-card border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <div className="empty-state-content">
              <div className="empty-icon mb-4">
                <i className="fas fa-clipboard-list"></i>
              </div>
              <h4 className="fw-bold text-dark mb-3">
                {filter === "all"
                  ? "No Orders Yet"
                  : `No ${
                      filter.charAt(0).toUpperCase() + filter.slice(1)
                    } Orders`}
              </h4>
              <p className="text-muted mb-4">
                {filter === "all"
                  ? "Orders from customers will appear here when they start purchasing."
                  : `No orders with ${filter} status found. Try a different filter.`}
              </p>
              <div className="decorative-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Card className="orders-table-card border-0 shadow-sm">
          <Card.Header className="admin-bg-gradient border-0">
            <div className="d-flex align-items-center">
              <i className="fas fa-clipboard-list text-white me-2"></i>
              <h5 className="text-white mb-0 fw-bold">
                {filter === "all"
                  ? "All Orders"
                  : `${
                      filter.charAt(0).toUpperCase() + filter.slice(1)
                    } Orders`}
              </h5>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0 admin-table">
                <thead>
                  <tr>
                    <th>
                      <i className="fas fa-calendar-alt me-2 text-primary"></i>
                      Date & Time
                    </th>
                    <th>
                      <i className="fas fa-hashtag me-2 text-primary"></i>
                      Order ID
                    </th>
                    <th>
                      <i className="fas fa-user me-2 text-primary"></i>
                      Customer
                    </th>
                    <th>
                      <i className="fas fa-info-circle me-2 text-primary"></i>
                      Status
                    </th>
                    <th className="text-end">
                      <i className="fas fa-money-bill-wave me-2 text-primary"></i>
                      Total Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order, index) => {
                    const statusConfig = getStatusConfig(order.status);
                    return (
                      <tr key={order._id} className="admin-order-row">
                        <td>
                          <div className="date-info">
                            <div className="fw-semibold">
                              {new Date(order.orderedOn).toLocaleDateString(
                                "en-US",
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                }
                              )}
                            </div>
                            <small className="text-muted">
                              {new Date(order.orderedOn).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </small>
                          </div>
                        </td>
                        <td>
                          <div className="admin-order-id-container">
                            <code className="admin-order-id">
                              #{order._id.slice(-8).toUpperCase()}
                            </code>
                          </div>
                        </td>
                        <td>
                          <div className="customer-info">
                            <div className="customer-email">
                              <i className="fas fa-user-circle text-primary me-2"></i>
                              {order.userId?.email || "N/A"}
                            </div>
                          </div>
                        </td>
                        <td>
                          <Badge
                            bg={statusConfig.bg}
                            text={statusConfig.text}
                            className="admin-status-badge"
                          >
                            <i
                              className={`fas fa-${statusConfig.icon} me-1`}
                            ></i>
                            {order.status}
                          </Badge>
                        </td>
                        <td className="text-end">
                          <div className="admin-total-amount">
                            <span className="currency">₱</span>
                            <span className="amount">
                              {order.totalPrice
                                ? order.totalPrice.toLocaleString("en-PH", {
                                    minimumFractionDigits: 2,
                                  })
                                : "0.00"}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Enhanced Admin Styles */}
      <style jsx>{`
        .admin-stats-card {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 15px;
        }

        .admin-stats-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
        }

        .admin-stat-icon {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          font-size: 1.5rem;
        }

        .filter-controls-card {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          border-radius: 15px;
        }

        .filter-btn {
          border-radius: 25px;
          padding: 0.5rem 1rem;
          font-weight: 600;
        }

        .empty-state-card {
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
          border-radius: 20px;
          position: relative;
          overflow: hidden;
        }

        .empty-state-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="adminEmptyPattern" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="%236f42c1" opacity="0.05"/></pattern></defs><rect width="100" height="100" fill="url(%23adminEmptyPattern)"/></svg>');
          pointer-events: none;
        }

        .empty-state-content {
          position: relative;
          z-index: 1;
        }

        .empty-icon {
          background: linear-gradient(135deg, #6f42c1, #495057);
          border-radius: 50%;
          width: 100px;
          height: 100px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          font-size: 2.5rem;
          color: white;
          animation: pulse 2s infinite;
        }

        .decorative-dots {
          display: flex;
          justify-content: center;
          gap: 8px;
        }

        .decorative-dots span {
          width: 8px;
          height: 8px;
          background: linear-gradient(135deg, #6f42c1, #495057);
          border-radius: 50%;
          animation: wave 1.5s ease-in-out infinite;
        }

        .decorative-dots span:nth-child(2) {
          animation-delay: 0.1s;
        }

        .decorative-dots span:nth-child(3) {
          animation-delay: 0.2s;
        }

        .orders-table-card {
          border-radius: 20px;
          overflow: hidden;
        }

        .admin-bg-gradient {
          background: linear-gradient(135deg, #6f42c1 0%, #495057 100%);
          padding: 1.25rem;
        }

        .admin-table th {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border: none;
          font-weight: 600;
          color: #495057;
          padding: 1rem;
          position: relative;
        }

        .admin-table th::after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, #6f42c1, #495057);
        }

        .admin-order-row {
          transition: all 0.3s ease;
        }

        .admin-order-row:hover {
          background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
          transform: scale(1.01);
        }

        .admin-order-row td {
          padding: 1rem;
          vertical-align: middle;
          border: none;
          border-bottom: 1px solid #e9ecef;
        }

        .admin-order-id {
          background: linear-gradient(135deg, #e8e6ff 0%, #f0efff 100%);
          color: #6f42c1;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
          border: 1px solid rgba(111, 66, 193, 0.2);
        }

        .admin-status-badge {
          padding: 0.5rem 0.75rem;
          border-radius: 25px;
          font-size: 0.85rem;
          font-weight: 600;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .customer-info {
          display: flex;
          flex-direction: column;
        }

        .customer-email {
          font-weight: 600;
          color: #495057;
          font-size: 0.9rem;
        }

        .admin-total-amount {
          font-size: 1.2rem;
          font-weight: 700;
        }

        .admin-total-amount .currency {
          color: #6f42c1;
        }

        .admin-total-amount .amount {
          color: #6f42c1;
        }

        @keyframes pulse {
          0%,
          100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        @keyframes wave {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @media (max-width: 768px) {
          .admin-stats-card {
            margin-bottom: 1rem;
          }

          .admin-table {
            font-size: 0.85rem;
          }

          .admin-order-row td {
            padding: 0.75rem 0.5rem;
          }

          .customer-email {
            font-size: 0.8rem;
          }

          .admin-total-amount {
            font-size: 1rem;
          }

          .filter-controls-card .d-flex {
            flex-direction: column;
            gap: 1rem;
          }
        }
      `}</style>
    </Container>
  );
}

export default OrdersAdminView;
